/**
 * Webhook Event Enums
 *
 * Enums for business webhook event types and delivery status.
 * Note: WebhookEvent (messaging events) is defined in roles.ts
 * Issue #313 - Centralize enums to shared-types
 */

import { z } from 'zod';

// ============================================================================
// BUSINESS WEBHOOK EVENTS
// ============================================================================

/**
 * Business webhook event types
 * Events that can trigger webhook deliveries for business entities
 * Note: Distinguished from WebhookEvent in roles.ts which covers messaging events
 */
export enum BusinessWebhookEvent {
  // Case events
  CASE_CREATED = 'case.created',
  CASE_UPDATED = 'case.updated',
  CASE_STATUS_CHANGED = 'case.status_changed',
  CASE_ASSIGNED = 'case.assigned',
  CASE_CLOSED = 'case.closed',
  CASE_ESCALATED = 'case.escalated',

  // Consumer events
  CONSUMER_CREATED = 'consumer.created',
  CONSUMER_UPDATED = 'consumer.updated',
  CONSUMER_MERGED = 'consumer.merged',

  // Task events
  TASK_CREATED = 'task.created',
  TASK_COMPLETED = 'task.completed',
  TASK_OVERDUE = 'task.overdue',

  // Document events
  DOCUMENT_UPLOADED = 'document.uploaded',
  DOCUMENT_SIGNED = 'document.signed',
}

/** Zod schema for BusinessWebhookEvent validation */
export const BusinessWebhookEventSchema = z.nativeEnum(BusinessWebhookEvent);

/** Array of all business webhook events for iteration */
export const BUSINESS_WEBHOOK_EVENTS = Object.values(BusinessWebhookEvent);

/** Business webhook events grouped by entity type */
export const BUSINESS_WEBHOOK_EVENTS_BY_ENTITY = {
  case: [
    BusinessWebhookEvent.CASE_CREATED,
    BusinessWebhookEvent.CASE_UPDATED,
    BusinessWebhookEvent.CASE_STATUS_CHANGED,
    BusinessWebhookEvent.CASE_ASSIGNED,
    BusinessWebhookEvent.CASE_CLOSED,
    BusinessWebhookEvent.CASE_ESCALATED,
  ],
  consumer: [
    BusinessWebhookEvent.CONSUMER_CREATED,
    BusinessWebhookEvent.CONSUMER_UPDATED,
    BusinessWebhookEvent.CONSUMER_MERGED,
  ],
  task: [BusinessWebhookEvent.TASK_CREATED, BusinessWebhookEvent.TASK_COMPLETED, BusinessWebhookEvent.TASK_OVERDUE],
  document: [BusinessWebhookEvent.DOCUMENT_UPLOADED, BusinessWebhookEvent.DOCUMENT_SIGNED],
} as const;

// ============================================================================
// WEBHOOK DELIVERY STATUS
// ============================================================================

/**
 * Webhook delivery status
 */
export enum WebhookDeliveryStatus {
  /** Delivery is pending */
  PENDING = 'pending',
  /** Delivery was successful (2xx response) */
  SUCCESS = 'success',
  /** Delivery failed (non-2xx response or network error) */
  FAILED = 'failed',
  /** Delivery is being retried */
  RETRYING = 'retrying',
}

/** Zod schema for WebhookDeliveryStatus validation */
export const WebhookDeliveryStatusSchema = z.nativeEnum(WebhookDeliveryStatus);

/** Array of all delivery statuses for iteration */
export const WEBHOOK_DELIVERY_STATUSES = Object.values(WebhookDeliveryStatus);

// ============================================================================
// WEBHOOK ENDPOINT STATUS
// ============================================================================

/**
 * Webhook endpoint status
 */
export enum WebhookEndpointStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

/** Zod schema for WebhookEndpointStatus validation */
export const WebhookEndpointStatusSchema = z.nativeEnum(WebhookEndpointStatus);
